import { useState, useMemo } from 'react';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Calendar } from '@design-system/Calendar';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@design-system/Dialog';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  useResumeNow,
  useResumeSubscription,
  useCanCustomResumeMutation,
  useGetPauseResumeDueDate,
  useCalculateResumeDatesMutation,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import {
  formatSMPDate,
  formatDateWithDashes,
  isSubscriptionPaused,
} from './utils/smpHelpers';
import { useUIMessages } from './hooks/useUIMessages';
import { SMPDialogContent, SMPMutationErrorDialog } from './components';
import { SMP_PATH } from '@/constants/paths';
import { logSMPMutationError } from '@/utils/sentryLogger';

type Step = 'method-selection' | 'calendar' | 'confirmation' | 'success' | 'success-custom';
type ResumeMethod = 'resume-now' | 'custom-date' | null;

// ============================================
// Radio Option Card
// ============================================

const RadioOptionCard = ({
  label,
  selected,
  onSelect,
  testId,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  testId: string;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={`
      flex items-center justify-between w-full p-4 rounded-xl border-2 transition-colors text-left
      ${selected ? 'border-success bg-white' : 'border-stroke-secondary-subtle bg-white'}
    `}
    data-testid={testId}
  >
    <span className="typography-body text-primary">{label}</span>
    <div
      className={`
        size-5 rounded-full border-2 flex items-center justify-center shrink-0
        ${selected ? 'border-success' : 'border-stroke-secondary-subtle'}
      `}
    >
      {selected && <div className="size-2.5 rounded-full bg-success" />}
    </div>
  </button>
);

// ============================================
// Resume In Progress Dialog
// ============================================

const ResumeInProgressDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
    <SMPDialogContent>
      <DialogHeader className="gap-6 text-start">
        <DialogTitle className="typography-h3 text-primary">
          Resume In Progress
        </DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-col gap-4">
            <p className="typography-body text-secondary">
              We are processing your recent resume request, this can take up to
              10 minutes to reflect in your account.
            </p>
            <p className="typography-body text-secondary">
              Once complete, you will be able to view or update your resume date
              again.
            </p>
          </div>
        </DialogDescription>
      </DialogHeader>
    </SMPDialogContent>
  </Dialog>
);

// ============================================
// Main Page
// ============================================

export const SMPResumeSubscriptionPage = () => {
  const { smpNavigate, smpGoBack } = useSMPNavigate();
  const {
    subscriptionId,
    subscriptionDetail,
    uiMessages,
    showMaintenance,
    isSessionReady,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const { getMessageText } = useUIMessages(uiMessages);

  const [step, setStep] = useState<Step>('method-selection');
  const [selectedMethod, setSelectedMethod] = useState<ResumeMethod>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [billingDate, setBillingDate] = useState<string>('');
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [rawNextOrderDate, setRawNextOrderDate] = useState<string>('');
  const [rawDeliveryDate, setRawDeliveryDate] = useState<string>('');
  const [nextOrderDate, setNextOrderDate] = useState<string>('');
  const [showResumeInProgress, setShowResumeInProgress] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const resumeNowMutation = useResumeNow();
  const resumeSubscriptionMutation = useResumeSubscription();
  const canCustomResumeMutation = useCanCustomResumeMutation();
  const calculateDatesMutation = useCalculateResumeDatesMutation();

  const productSku = subscriptionDetail?.line_items?.[0]?.sku ?? '';

  const {
    data: dueDateData,
    isLoading: isDueDateLoading,
  } = useGetPauseResumeDueDate(productSku, subscriptionId, {
    enabled: isSessionReady && !!subscriptionId && !!productSku,
  });

  const minDate = useMemo(() => new Date(), []);
  const maxDate = useMemo(
    () => (dueDateData?.maxDueDate ? new Date(dueDateData.maxDueDate) : new Date()),
    [dueDateData],
  );

  const goBack = () => smpGoBack();
  const isLoading = isSMPLoading || isDueDateLoading;

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </section>
    );
  }

  if (isSMPError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  if (!isSubscriptionPaused(subscriptionDetail.subscription_status) && step !== 'success' && step !== 'success-custom') {
    smpNavigate(SMP_PATH, { replace: true });
    return null;
  }

  const resumeDateFormatted = subscriptionDetail.resume_date
    ? formatSMPDate(subscriptionDetail.resume_date)
    : '';

  const handleMethodContinue = () => {
    if (selectedMethod === 'resume-now') {
      resumeNowMutation.mutate(subscriptionId, {
        onSuccess: (data: unknown) => {
          const res = data as { nextOrderDate?: string; next_order_date?: string };
          setNextOrderDate(
            formatSMPDate(res?.nextOrderDate || res?.next_order_date || ''),
          );
          setStep('success');
        },
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('resumeNow', error, {
            screen: 'SMPResumeSubscriptionPage',
            subscriptionId,
          });
        },
      });
    } else if (selectedMethod === 'custom-date') {
      canCustomResumeMutation.mutate(subscriptionId, {
        onSuccess: (response) => {
          if (response.canCustomResume) {
            setStep('calendar');
          } else {
            setShowResumeInProgress(true);
          }
        },
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('canCustomResume', error, {
            screen: 'SMPResumeSubscriptionPage',
            subscriptionId,
          });
        },
      });
    }
  };

  const handleCalendarContinue = () => {
    calculateDatesMutation.mutate(
      {
        subscriptionId,
        deliveryDate: formatDateWithDashes(selectedDate),
      },
      {
        onSuccess: (response) => {
          setRawNextOrderDate(response.next_order_date);
          setRawDeliveryDate(response.estimated_delivery_date);
          setBillingDate(formatSMPDate(response.next_order_date));
          setDeliveryDate(formatSMPDate(response.estimated_delivery_date));
          setStep('confirmation');
        },
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('calculateResumeDates', error, {
            screen: 'SMPResumeSubscriptionPage',
            subscriptionId,
          });
        },
      },
    );
  };

  const handleConfirmResume = () => {
    resumeSubscriptionMutation.mutate(
      {
        subscriptionId,
        resumeDate: formatDateWithDashes(selectedDate),
        nextOrderDate: rawNextOrderDate,
        estimatedDeliveryDate: rawDeliveryDate,
      },
      {
        onSuccess: (data: unknown) => {
          const res = data as { nextOrderDate?: string; next_order_date?: string };
          setNextOrderDate(
            formatSMPDate(
              res?.nextOrderDate ||
              res?.next_order_date ||
              billingDate,
            ),
          );
          setStep('success-custom');
        },
        onError: (error) => {
          setShowErrorDialog(true);
          logSMPMutationError('resumeSubscription', error, {
            screen: 'SMPResumeSubscriptionPage',
            subscriptionId,
          });
        },
      },
    );
  };

  const handleResumeInProgressClose = () => {
    setShowResumeInProgress(false);
    goBack();
  };

  const isSubmitting =
    resumeNowMutation.isPending ||
    resumeSubscriptionMutation.isPending ||
    canCustomResumeMutation.isPending ||
    calculateDatesMutation.isPending;

  const handleNavBack = () => {
    switch (step) {
      case 'calendar':
        setStep('method-selection');
        break;
      case 'confirmation':
        setStep('calendar');
        break;
      default:
        goBack();
    }
  };

  const getButtonLabel = () => {
    if (selectedMethod === 'resume-now') return 'Submit';
    return 'Continue';
  };

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-resumePage"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={step === 'success' ? goBack : handleNavBack}
        />
        {step === 'success' && (
          <h1 className="typography-h6 text-primary" data-testid="smp-resume-successTitle">
            Subscription Status Updated
          </h1>
        )}
      </div>

      {/* Step 1: Method Selection */}
      {step === 'method-selection' && (
        <div className="flex flex-col gap-8" data-testid="smp-resume-methodStep">
          <div className="flex flex-col gap-4">
            <h2
              className="typography-h2 text-primary"
              data-testid="smp-resume-methodTitle"
            >
              {getMessageText(
                'smp_resume_methods_description_2',
                'headerText',
                { resumeDate: resumeDateFormatted },
                `Your current resume date is ${resumeDateFormatted}. Would you like to resume now or resume on a different date?`,
              )}
            </h2>
            <p className="typography-body text-secondary">
              {getMessageText(
                'smp_resume_methods_description_2',
                'bodyText',
                undefined,
                'If you choose to resume now, your subscription will resume immediately, and your next order will be placed within 7 days.',
              )}
            </p>
          </div>

          <div className="flex flex-col gap-3 w-full max-w-sm">
            <RadioOptionCard
              label="Resume now"
              selected={selectedMethod === 'resume-now'}
              onSelect={() => setSelectedMethod('resume-now')}
              testId="smp-resume-optionNow"
            />
            <RadioOptionCard
              label="Resume on a different date"
              selected={selectedMethod === 'custom-date'}
              onSelect={() => setSelectedMethod('custom-date')}
              testId="smp-resume-optionCustom"
            />
          </div>

          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={handleMethodContinue}
              disabled={!selectedMethod || isSubmitting}
              data-testid="smp-resume-methodContinue"
            >
              {isSubmitting ? 'Processing...' : getButtonLabel()}
            </Button>
            <Button
              variant="secondary"
              onClick={goBack}
              disabled={isSubmitting}
              data-testid="smp-resume-methodCancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Calendar Date Picker */}
      {step === 'calendar' && (
        <div className="flex flex-col gap-8" data-testid="smp-resume-calendarStep">
          <div className="flex flex-col gap-4">
            <p
              className="text-xl/snug font-light text-primary"
              data-testid="smp-resume-calendarTitle"
            >
              {getMessageText(
                'smp_resume_calendar_description',
                'headerText',
                undefined,
                "Using the calendar below, select the date you'd like to resume your subscription.",
              )}
            </p>
            <p className="typography-body text-primary">
              {getMessageText(
                'smp_resume_calendar_description',
                'bodyText',
                undefined,
                'Within 7 days after your resume date, you will be billed and your next order will be placed.',
              )}
            </p>
          </div>

          <div className="[&>div]:items-start max-w-full overflow-x-auto">
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  setSelectedDate(date);
                  setCalendarMonth(date);
                }
              }}
              fromDate={minDate}
              toDate={maxDate}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
            />
          </div>

          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={handleCalendarContinue}
              disabled={isSubmitting}
              data-testid="smp-resume-calendarContinue"
            >
              {isSubmitting ? 'Processing...' : 'Continue'}
            </Button>
            <Button
              variant="secondary"
              onClick={goBack}
              disabled={isSubmitting}
              data-testid="smp-resume-calendarCancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Date Confirmation */}
      {step === 'confirmation' && (
        <div
          className="flex flex-col gap-8"
          data-testid="smp-resume-confirmStep"
        >
          <p className="text-xl/snug font-light text-primary">
            {getMessageText(
              'smp_pause_calendar_confirmation',
              'bodyText',
              { billedDate: billingDate, deliveryDate },
              `Based on the date you chose, you will be billed on ${billingDate} and the estimated delivery date of your next order is ${deliveryDate}. Is this what you want to do?`,
            )}
          </p>

          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={handleConfirmResume}
              disabled={isSubmitting}
              data-testid="smp-resume-confirmYes"
            >
              {isSubmitting ? 'Processing...' : 'Yes'}
            </Button>
            <Button
              variant="secondary"
              onClick={goBack}
              disabled={isSubmitting}
              data-testid="smp-resume-confirmCancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Step 4a: Resume Now — inline success page (per Figma) */}
      {step === 'success' && (
        <div className="flex flex-col gap-12" data-testid="smp-resume-successStep">
          <div className="flex flex-col gap-6">
            <p className="typography-h3 text-primary">
              Great! Your subscription is active again.
            </p>
            {(() => {
              const htmlContent = getMessageText(
                'smp_resume_now_submit_alert',
                'bodyHtml',
                { billedDate: nextOrderDate },
              );
              if (htmlContent) {
                return (
                  <div
                    className="typography-body text-primary [&_p]:mb-3"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                  />
                );
              }
              return (
                <p className="typography-body text-primary">
                  Your subscription has resumed and your next order will be placed on{' '}
                  {nextOrderDate}.
                </p>
              );
            })()}
          </div>

          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={goBack}
              data-testid="smp-resume-returnToAccount"
            >
              Return to My Account
            </Button>
          </div>
        </div>
      )}

      {/* Step 4b: Custom Date — success dialog */}
      <Dialog open={step === 'success-custom'} onOpenChange={(isOpen) => { if (!isOpen) goBack(); }}>
        <SMPDialogContent data-testid="smp-resume-successCustomStep">
          <DialogHeader className="gap-6 text-start">
            <DialogDescription asChild>
              <div className="flex flex-col gap-3">
                {(() => {
                  const htmlContent = getMessageText(
                    'smp_resume_submit_alert',
                    'bodyHtml',
                    { billedDate: billingDate, deliveryDate },
                  );
                  if (htmlContent) {
                    return (
                      <div
                        className="typography-body text-secondary [&_p]:mb-3"
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                      />
                    );
                  }
                  return (
                    <>
                      <p className="typography-body text-secondary">
                        Great! Your subscription is active again.
                      </p>
                      <p className="typography-body text-secondary">
                        Your subscription has resumed and your next order will be placed on{' '}
                        {nextOrderDate}.
                      </p>
                    </>
                  );
                })()}
              </div>
            </DialogDescription>
          </DialogHeader>
        </SMPDialogContent>
      </Dialog>

      {/* Resume In Progress Dialog */}
      <ResumeInProgressDialog
        open={showResumeInProgress}
        onClose={handleResumeInProgressClose}
      />

      <SMPMutationErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        description="Something went wrong while resuming your subscription. Please try again."
      />
    </section>
  );
};
